import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Mail,
  MailOpen,
  FolderKanban,
  MessageSquare,
  X,
  Save,
  Home,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPANIES = [
  { slug: 'builder', name: 'Ananthameenakshi & Sriram Associates' },
  { slug: 'developers', name: 'Ananthameenakshi Developers' },
  { slug: 'architecture', name: 'Ananthameenakshi Architects' },
  { slug: 'materials', name: 'Ananthameenakshi & Meenakshi Agency' },
];

export default function AdminDashboard() {
  const { token, isAuthenticated, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProjectModal, setShowProjectModal] = useState(false); // using different state of prokects and package due to use of same modal for both
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    company_slug: 'builder',
    title: '',
    description: '',
    image_url: '',
    location: '',
    year: new Date().getFullYear().toString(),
  });

const [packages, setPackages] = useState([]);
const [editingPackage, setEditingPackage] = useState(null);

const [packageForm, setPackageForm] = useState({
  company_slug: "builder",
  title: "",
  price: "",
  image_url: "",
  description: "",
  features: "",
});

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (token) {
      fetchData();
    }// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

   const savePackage = async (e) => {
  e.preventDefault();

  const payload = {
    ...packageForm,
    features: packageForm.features
      .split(",")
      .map((f) => f.trim()),
  };

  try {
    if (editingPackage) {
      await axios.put(`${API}/admin/packages/${editingPackage.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } else {
      await axios.post(`${API}/admin/packages`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
    }

    setShowPackageModal(false);
    fetchData();
  } catch (error) {
    console.error("Error saving package:", error);
  }
};
   const deletePackage = async (id) => {

    if (!window.confirm("Delete this package?")) return;

    await axios.delete(`${API}/admin/packages/${id}`, {
     headers: { Authorization: `Bearer ${token}` },
     });

   fetchData();
    };

  const fetchData = async () => {
    setLoading(true);
    try {
     const [projectsRes, contactsRes, packagesRes] = await Promise.all([
     axios.get(`${API}/admin/projects`, {
      headers: { Authorization: `Bearer ${token}` },
     }),
    axios.get(`${API}/admin/contacts`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
    axios.get(`${API}/packages`,{
      headers: { Authorization: `Bearer ${token}` },
    })
    
]);
   
setProjects(projectsRes.data);
setContacts(contactsRes.data);
setPackages(packagesRes.data);
    }
     catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  const openCreateModal = () => {
    setEditingProject(null);
    setFormData({
      company_slug: 'builder',
      title: '',
      description: '',
      image_url: '',
      location: '',
      year: new Date().getFullYear().toString(),
    });
    setShowProjectModal(true);
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setFormData({
      company_slug: project.company_slug,
      title: project.title,
      description: project.description,
      image_url: project.image_url,
      location: project.location,
      year: project.year,
    });
    setShowProjectModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await axios.put(`${API}/admin/projects/${editingProject.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API}/admin/projects`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setShowProjectModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API}/admin/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (error) {
      console.error('Error marking contact as read:', error);
    }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const unreadCount = contacts.filter((c) => !c.read).length;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="bg-obsidian-paper border-b border-white/5 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <span className="text-white font-heading font-bold text-xl">A</span>
            </div>
            <div>
              <h1 className="font-heading text-xl font-semibold text-white">Admin Panel</h1>
              <p className="text-gray-500 text-xs">AnanthaMeenakshi  Arch Group</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-gray-400 hover:text-gold transition-colors flex items-center gap-2 text-sm"
              data-testid="view-site-link"
            >
              <Home size={16} />
              <span className="hidden sm:inline">View Site</span>
            </Link>
            <button
              onClick={handleLogout}
              className="text-gray-400 hover:text-primary transition-colors flex items-center gap-2 text-sm"
              data-testid="logout-btn"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('projects')}
              className={`py-4 px-2 text-sm font-medium uppercase tracking-wider border-b-2 transition-colors ${
                activeTab === 'projects'
                  ? 'border-gold text-gold'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
              data-testid="tab-projects"
            >
              <FolderKanban size={16} className="inline mr-2" />
              Projects ({projects.length})
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`py-4 px-2 text-sm font-medium uppercase tracking-wider border-b-2 transition-colors relative ${
                activeTab === 'contacts'
                  ? 'border-gold text-gold'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
              data-testid="tab-contacts"
            >
              <MessageSquare size={16} className="inline mr-2" />
              Contacts ({contacts.length})
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-2 w-5 h-5 bg-primary text-white text-xs flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("packages")}
               className={`py-4 px-2 text-sm font-medium uppercase tracking-wider border-b-2 transition-colors 
                ${
                 activeTab === "packages"
                  ? "border-gold text-gold" : "border-transparent text-gray-400 hover:text-white"  }`}>
                 <FolderKanban size={16} className="inline mr-2" />
                 Packages ({packages.length})
             </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Package Tab */}
        {activeTab === "packages" && (
  <div>
         <div className="flex items-center justify-between mb-8">
           <h2 className="font-heading text-2xl font-bold text-white">
              Current Packages
            </h2>

            <button
               onClick={() => {
              setEditingPackage(null);
              setPackageForm({
              company_slug: 'builder',
              title: "",
              price: "",
              image_url: "",
              description: "",
              features: "",
                 });
               setShowPackageModal(true);
                  }}
                className="btn-gold flex items-center gap-2 text-sm">
               <Plus size={16} />
               Add Package
             </button>
         </div>

         <div className="grid md:grid-cols-3 gap-6">
           {packages.map((pkg) => (
            <div
             key={pkg.id}
            className=" rounded-2xl bg-obsidian-paper border border-white/10 p-4"
            >

            <img
            src={pkg.image_url}
            className="w-full h-40 object-cover mb-4"
            />

            <h3 className="text-white font-semibold">
            {pkg.title}
            </h3>

             <p className="text-gold mb-2">
            {pkg.price}
              </p>
              
               <p className="text-gray-400 text-sm capitalize">
                {pkg.company_slug}</p>

            <div className="flex gap-3 mt-3">

            <button
              onClick={() => {
                setEditingPackage(pkg);
                setPackageForm({
                  company_slug: pkg.company_slug,
                  title: pkg.title,
                  price: pkg.price,
                  image_url: pkg.image_url,
                  description: pkg.description,
                  features: pkg.features.join(", "),
                });
                setShowPackageModal(true);
              }}
              className="text-gold"
            >
              <Pencil size={16}/>
            </button>

            <button
              onClick={() => deletePackage(pkg.id)}
              className="text-red-400"
            >
              <Trash2 size={16}/>
            </button>

          </div>

        </div>
      ))}

    </div>

  </div>
)}
        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div data-testid="projects-panel">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl font-bold text-white">Manage Projects</h2>
              <button
                onClick={openCreateModal}
                className="btn-gold flex items-center gap-2 text-sm"
                data-testid="add-project-btn"
              >
                <Plus size={16} />
                Add Project
              </button>
            </div>

            {projects.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                No projects yet. Add your first project!
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="admin-table w-full">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Title</th>
                      <th>Company</th>
                      <th>Location</th>
                      <th>Year</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => (
                      <tr key={project.id} data-testid={`project-row-${project.id}`}>
                        <td>
                          <img
                            src={project.image_url}
                            alt={project.title}
                            className="w-16 h-12 object-cover"
                          />
                        </td>
                        <td className="text-white font-medium">{project.title}</td>
                        <td className="text-gray-400 text-sm capitalize">{project.company_slug}</td>
                        <td className="text-gray-400 text-sm">{project.location}</td>
                        <td className="text-gray-400 text-sm">{project.year}</td>
                        <td className="text-right">
                          <button
                            onClick={() => openEditModal(project)}
                            className="text-gold hover:text-gold-light p-2"
                            data-testid={`edit-project-${project.id}`}
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteProject(project.id)}
                            className="text-primary hover:text-red-400 p-2"
                            data-testid={`delete-project-${project.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div data-testid="contacts-panel">
            <h2 className="font-heading text-2xl font-bold text-white mb-8">Contact Submissions</h2>

            {contacts.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                No contact submissions yet.
              </div>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className={`bg-obsidian-paper border p-6 ${
                      contact.read ? 'border-white/5' : 'border-gold/30'
                    }`}
                    data-testid={`contact-card-${contact.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {contact.read ? (
                            <MailOpen size={18} className="text-gray-500" />
                          ) : (
                            <Mail size={18} className="text-gold" />
                          )}
                          <h3 className="text-white font-semibold">{contact.name}</h3>
                          {!contact.read && (
                            <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs uppercase">
                              New
                            </span>
                          )}
                        </div>
                        <div className="text-gray-400 text-sm mb-3">
                          <span>{contact.email}</span>
                          {contact.phone && <span> • {contact.phone}</span>}
                          {contact.company && <span> • {contact.company}</span>}
                        </div>
                        <p className="text-gray-300 text-sm">{contact.message}</p>
                        <p className="text-gray-500 text-xs mt-3">
                          {new Date(contact.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!contact.read && (
                          <button
                            onClick={() => handleMarkRead(contact.id)}
                            className="text-gold hover:text-gold-light p-2"
                            title="Mark as read"
                            data-testid={`mark-read-${contact.id}`}
                          >
                            <MailOpen size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteContact(contact.id)}
                          className="text-primary hover:text-red-400 p-2"
                          title="Delete"
                          data-testid={`delete-contact-${contact.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Project Modal */}
          
      {showProjectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" data-testid="project-modal">
          <div className="bg-obsidian-paper border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-white/5">
              <h3 className="font-heading text-xl font-semibold text-white">
                {editingProject ? 'Edit Project' : 'Add New Project'}
              </h3>
              <button
                onClick={() => setShowProjectModal(false)}
                className="text-gray-400 hover:text-white"
                data-testid="close-modal"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Company *</label>
                <select
                  value={formData.company_slug}
                  onChange={(e) => setFormData({ ...formData, company_slug: e.target.value })}
                  className="w-full bg-obsidian border border-white/10 px-4 py-3 text-white"
                  data-testid="project-company"
                >
                 
                  {COMPANIES.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="input-dark w-full border border-white/10 px-4 py-3"
                  data-testid="project-title"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  className="input-dark w-full border border-white/10 px-4 py-3 resize-none"
                  data-testid="project-description"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Image URL *</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  required
                  placeholder="https://..."
                  className="input-dark w-full border border-white/10 px-4 py-3"
                  data-testid="project-image"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Location *</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    className="input-dark w-full border border-white/10 px-4 py-3"
                    data-testid="project-location"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Year *</label>
                  <input
                    type="text"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    required
                    className="input-dark w-full border border-white/10 px-4 py-3"
                    data-testid="project-year"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Price</label>
                 <input
                  type="text"
                  placeholder="Price"
                  value={formData.price}
                   onChange={(e)=>setFormData({...formData, price:e.target.value})}
                   className="input-dark w-full border border-white/10 px-4 py-3"
                  />
                </div>
                {/* PROJECT FIELDS */}

              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowProjectModal(false)}
                  className="flex-1 border border-white/20 text-gray-400 py-3 hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-gold flex items-center justify-center gap-2"
                  data-testid="save-project"
                >
                  <Save size={16} />
                  {editingProject ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/*  package Modal */}
      
{showPackageModal && (
<div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">

<div className="bg-obsidian-paper border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">

{/* Header */}
<div className="flex items-center justify-between p-6 border-b border-white/5">

<h3 className="font-heading text-xl font-semibold text-white">

{activeTab === "packages"
 ? editingPackage
   ? "Edit Builder Package"
   : "Add Builder Package"
 : editingProject
   ? "Edit Project"
   : "Add New Project"}

</h3>

<button
onClick={() => setShowPackageModal(false)}
className="text-gray-400 hover:text-white"
>
<X size={24}/>
</button>

</div>

{/* FORM */}

<form
onSubmit={savePackage }
className="p-6 space-y-6"
>

{/* COMPANY SELECT (ONLY FOR PROJECTS) */}



<div>

<label className="block text-gray-400 text-sm mb-2">
Company *
</label>

<select
value={packageForm.company_slug}
onChange={(e) => setPackageForm({ ...packageForm, company_slug: e.target.value })
}
className="w-full bg-obsidian border border-white/10 px-4 py-3 text-white"
>
  

{COMPANIES.map((c)=>(
<option key={c.slug} value={c.slug}>
{c.name}
</option>
))}

</select>

</div>



{/* TITLE */}

<div>
  

<label className="block text-gray-400 text-sm mb-2">
Title *
</label>

<input
type="text"
value={ packageForm.title }
onChange={(e)=> setPackageForm({...packageForm,title:e.target.value})
}
required
className="input-dark w-full border border-white/10 px-4 py-3"
/>

</div>

{/* DESCRIPTION */}

<div>

<label className="block text-gray-400 text-sm mb-2">
Description *
</label>

<textarea
rows={3}
value={ packageForm.description}
onChange={(e)=>setPackageForm({...packageForm,description:e.target.value})}
required
className="input-dark w-full border border-white/10 px-4 py-3 resize-none"
/>

</div>

{/* IMAGE */}

<div>

<label className="block text-gray-400 text-sm mb-2">
Image URL *
</label>

<input
type="url"
placeholder="https://..."
value={packageForm.image_url}
onChange={(e)=>setPackageForm({...packageForm,image_url:e.target.value})
}
required
className="input-dark w-full border border-white/10 px-4 py-3"
/>

</div>



{/* PACKAGE PRICE */}

{activeTab === "packages" && (

<div>

<label className="block text-gray-400 text-sm mb-2">
Price *
</label>

<input
type="text"
placeholder="₹2500 / sq.ft"
value={packageForm.price}
onChange={(e)=>setPackageForm({...packageForm,price:e.target.value})}
required
className="input-dark w-full border border-white/10 px-4 py-3"
/>

</div>

)}

{/* PACKAGE FEATURES */}

{activeTab === "packages" && (

<div>

<label className="block text-gray-400 text-sm mb-2">
Features (comma separated)
</label>

<input
type="text"
placeholder="Premium Cement, Smart Wiring, Architect Supervision"
value={packageForm.features}
onChange={(e)=>setPackageForm({...packageForm,features:e.target.value})}
className="input-dark w-full border border-white/10 px-4 py-3"
/>

</div>

)}

{/* FOOTER */}

<div className="flex gap-4 pt-4">

<button
type="button"
onClick={()=>setShowPackageModal(false)}
className="flex-1 border border-white/20 text-gray-400 py-3 hover:bg-white/5 transition-colors"
>
Cancel
</button>

<button
type="submit"
className="flex-1 btn-gold flex items-center justify-center gap-2"
>

<Save size={16}/>

{editingProject || editingPackage ? "Update" : "Create"}

</button>

</div>

</form>

</div>

</div>
)}                                                
                      
    </div>
  );
}
                                                                     
